import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Zuriya';
const DEFAULT_DESCRIPTION = 'Discover and book inclusive multi-cultural hair, nails, lashes and beauty services across the UK. Verified providers, easy booking, real reviews.';
const DEFAULT_IMAGE = '';

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  noindex = false,
  jsonLd,
}) {
  // Browser tab: clean and short
  const tabTitle = title ? (title.includes(SITE_NAME) ? title : `${title}, ${SITE_NAME}`) : SITE_NAME;
  // SEO meta: descriptive for search engines
  const seoTitle = title ? (title.includes(SITE_NAME) ? title : `${title}, ${SITE_NAME}`) : `${SITE_NAME}, Inclusive Multi-Cultural Beauty Marketplace`;
  const canonicalUrl = url || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : '');

  return (
    <Helmet>
      <title>{tabTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph, uses SEO title for rich previews */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      {image && <meta property="og:image" content={image} />}
      <meta property="og:locale" content="en_GB" />

      {/* Twitter Card, uses SEO title */}
      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

/* Structured data builders */

export function buildLocalBusinessSchema(provider) {
  if (!provider) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BeautySalon',
    name: provider.brand_name || provider.name,
    description: provider.bio || `Inclusive multi-cultural beauty services in ${provider.location || 'the UK'}`,
    url: provider.username ? `${window.location.origin}/@${provider.username}` : undefined,
    image: provider.profile_image || undefined,
    address: provider.location ? {
      '@type': 'PostalAddress',
      addressLocality: provider.location,
      addressCountry: 'GB',
    } : undefined,
    aggregateRating: provider.rating > 0 ? {
      '@type': 'AggregateRating',
      ratingValue: provider.rating.toFixed(1),
      reviewCount: provider.review_count || 0,
      bestRating: '5',
      worstRating: '1',
    } : undefined,
    priceRange: '££',
  };
}

export function buildServiceSchema(service, provider) {
  if (!service) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description || `${service.name} by ${provider?.brand_name || 'Zuriya'}`,
    url: `${window.location.origin}/service/${service.slug || service.service_id}`,
    provider: provider ? {
      '@type': 'BeautySalon',
      name: provider.brand_name || provider.name,
      url: provider.username ? `${window.location.origin}/@${provider.username}` : undefined,
    } : undefined,
    offers: {
      '@type': 'Offer',
      price: service.price?.toFixed(2),
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
    },
    serviceType: service.category,
  };

  if (service.reviews && service.reviews.length > 0) {
    const avgRating = service.reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / service.reviews.length;
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: avgRating.toFixed(1),
      reviewCount: service.reviews.length,
      bestRating: '5',
      worstRating: '1',
    };
    schema.review = service.reviews.slice(0, 3).map(r => ({
      '@type': 'Review',
      reviewRating: { '@type': 'Rating', ratingValue: r.rating },
      author: { '@type': 'Person', name: r.client_name || 'Client' },
      reviewBody: r.text || '',
    }));
  }

  return schema;
}

export function buildBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url || undefined,
    })),
  };
}

export function buildWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Zuriya',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: DEFAULT_DESCRIPTION,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${typeof window !== 'undefined' ? window.location.origin : ''}/discover?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}
